import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import { NumberField } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import useGridAndSystemProtectionMutation from '../hooks/use-grid-and-system-protection-mutation'
import useSyncFormWithValues from '../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../types/common'
import { FrequencyMonitoringSettingsModel } from '../types/grid-and-system-protection.model'
import { FrequencyMonitoringSettingsForm, getFrequencyMonitoringSettingsSchema } from '../types/schemas'
import { getChangedFieldStyle, isFieldDisabled } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
const NAME_SPACE = 'systemSettings.grid-and-system-protection-configuration.frequency-monitoring'

type Props = {
  frequencyMonitoringSettingsModel: FrequencyMonitoringSettingsModel
}

const FrequencyMonitoringSettings: React.FC<Props> = ({ frequencyMonitoringSettingsModel }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights

  const defaultValues = useMemo(() => getDefaultValues(frequencyMonitoringSettingsModel), [frequencyMonitoringSettingsModel])
  const methods = useForm<FrequencyMonitoringSettingsForm>({
    resolver: zodResolver(getFrequencyMonitoringSettingsSchema(frequencyMonitoringSettingsModel)),
    defaultValues,
    mode: 'onBlur',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { isLoading, updateGridAndSystemProtection } = useGridAndSystemProtectionMutation()

  const onSubmit = useCallback(
    async (data: FrequencyMonitoringSettingsForm): Promise<void> => {
      try {
        await updateGridAndSystemProtection({ frequencyMonitoringSettings: data }, FUNCTION_NUMBER.frequencyMonitoringSettings)
        methods.reset(data)
      } catch (error) {
        methods.reset()
      }
    },
    [methods, updateGridAndSystemProtection]
  )

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              <SettingsBox
                title={t(`${NAME_SPACE}.frequency-drop-protection.title`)}
                suffix={
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <NumberField
                      name="frequencyDropProtection.frequency"
                      label={t(`${NAME_SPACE}.frequency-drop-protection.frequency.label`)}
                      suffix={UnitTypes.frequency}
                      disabled={!canEdit || isFieldDisabled(frequencyMonitoringSettingsModel.frequencyDropProtection.frequency)}
                      sx={getChangedFieldStyle(frequencyMonitoringSettingsModel.frequencyDropProtection.frequency.changed)}
                    />
                    <NumberField
                      name="frequencyDropProtection.timeSeconds"
                      label={t(`${NAME_SPACE}.frequency-drop-protection.time.label`)}
                      suffix={UnitTypes.second}
                      disabled={!canEdit || isFieldDisabled(frequencyMonitoringSettingsModel.frequencyDropProtection.timeSeconds)}
                      sx={getChangedFieldStyle(frequencyMonitoringSettingsModel.frequencyDropProtection.timeSeconds.changed)}
                    />
                  </Box>
                }
              />
              <SettingsBox
                title={t(`${NAME_SPACE}.frequency-gain-protection.title`)}
                suffix={
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <NumberField
                      name="frequencyGainProtection.frequency"
                      label={t(`${NAME_SPACE}.frequency-gain-protection.frequency.label`)}
                      suffix={UnitTypes.frequency}
                      disabled={!canEdit || isFieldDisabled(frequencyMonitoringSettingsModel.frequencyGainProtection.frequency)}
                      sx={getChangedFieldStyle(frequencyMonitoringSettingsModel.frequencyGainProtection.frequency.changed)}
                    />
                    <NumberField
                      name="frequencyGainProtection.timeSeconds"
                      label={t(`${NAME_SPACE}.frequency-gain-protection.time.label`)}
                      suffix={UnitTypes.second}
                      disabled={!canEdit || isFieldDisabled(frequencyMonitoringSettingsModel.frequencyGainProtection.timeSeconds)}
                      sx={getChangedFieldStyle(frequencyMonitoringSettingsModel.frequencyGainProtection.timeSeconds.changed)}
                    />
                  </Box>
                }
              />
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

export default FrequencyMonitoringSettings

const getDefaultValues = (data: FrequencyMonitoringSettingsModel): FrequencyMonitoringSettingsForm => {
  return {
    frequencyGainProtection: {
      frequency: data.frequencyGainProtection.frequency.value,
      timeSeconds: data.frequencyGainProtection.timeSeconds.value,
    },
    frequencyDropProtection: {
      frequency: data.frequencyDropProtection.frequency.value,
      timeSeconds: data.frequencyDropProtection.timeSeconds.value,
    },
  }
}
